/**
 * Контракт журнала аудита, общий для бэкенда и SPA.
 *
 * Аудит — отдельный журнал действий пользователей (в отличие от технического лога
 * `logger.ts`, который пишет исходы операций в stdout). Записи аудита хранятся
 * файлами по дням и показываются в разделе «Аудит» экрана «Администратор»:
 * «кто, когда и что изменил». Событие собирается на сервере (`auditService.ts`),
 * поэтому SPA видит уже готовую фразу и не знает кодов событий.
 */

/** Период показа журнала: сутки или неделя — скользящее окно от текущего момента. */
export type AuditPeriod = 'day' | 'week';

/** Варианты дропдауна «Период» в порядке отображения (первый — по умолчанию). */
export const AUDIT_PERIODS: { value: AuditPeriod; label: string; ms: number }[] = [
  { value: 'day', label: 'За день', ms: 24 * 60 * 60 * 1000 },
  { value: 'week', label: 'За неделю', ms: 7 * 24 * 60 * 60 * 1000 },
];

/**
 * Сколько записей отдаёт экран аудита. Журнал читается целиком за период (файлы
 * дня/недели), но ответ ограничен: тысячи строк не нужны ни таблице, ни человеку.
 * Признак `truncated` говорит, что показаны не все записи периода.
 */
export const AUDIT_SCREEN_LIMIT = 500;

/**
 * Текст сбоя чтения журнала — один на два случая: ответ `502` эндпоинта журнала и
 * `audit: null` в ответе экрана «Администратор» (задача 38, этап C). Пользователь должен
 * видеть одно и то же сообщение, чем бы чтение ни закончилось, а не два разных текста.
 */
export const AUDIT_UNAVAILABLE_MESSAGE =
  'Не удалось прочитать журнал аудита. Попробуйте позже.';

/** Одна запись журнала в том виде, в каком её показывает экран. */
export interface AuditEntry {
  /** Идентификатор записи (uuid): строки таблицы различаются и при равном времени. */
  id: string;
  /** Момент события (ISO-строка, UTC). */
  time: string;
  /** Код события из каталога (`src/backend/audit/auditEventCatalog.ts`). */
  event: string;
  /** Готовая фраза: кто, что и с чем сделал. */
  description: string;
}

/** Ответ `GET /api/admin/audit`: период, границы окна и записи (сначала свежие). */
export interface AuditScreen {
  period: AuditPeriod;
  /** Начало окна (ISO-строка, UTC). */
  from: string;
  /** Конец окна (ISO-строка, UTC) — момент запроса. */
  to: string;
  /** Сколько записей попало в окно (до ограничения выдачи). */
  total: number;
  /** true — записей больше, чем отдано (`AUDIT_SCREEN_LIMIT`). */
  truncated: boolean;
  entries: AuditEntry[];
}
